import { API_CONFIG } from '@/api/config';
import { getSettings } from '@/api/generated/fintecyWealthManagerAPI.ts';
import { authService, preferencesService } from '@/api/services';
import { jwtDecode } from 'jwt-decode';

export interface GoogleUserInfo {
  // Unique identifiers
  sub: string; // Unique Google ID (most reliable unique identifier)
  email: string; // User's email address
  email_verified: boolean; // Whether the email is verified

  // Profile information
  name: string; // Full name
  given_name: string; // First name
  family_name: string; // Last name
  picture: string; // Profile picture URL
  locale: string; // User's locale (e.g., 'en')

  // Additional fields
  iss: string; // Token issuer (e.g., 'https://accounts.google.com')
  aud: string; // Audience (your client ID)
  iat: number; // Token issued at timestamp
  exp: number; // Token expiration timestamp
  azp: string; // Authorized party (your client ID)
}

export interface StoredUserInfo {
  googleSub: string;
  email: string;
  emailVerified: boolean;
  name: string;
  firstName: string;
  lastName: string;
  profilePicture: string;
  locale: string;
  provider: 'google';
  lastLogin: string;
}

export const STORAGE_KEYS = {
  USER_INFO: 'user_info',
  AUTH_TOKEN: 'auth_token',
} as const;

const storeUserInfo = (userInfo: StoredUserInfo) => {
  localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(userInfo));
};

export const googleAuthService = {
  async handleGoogleLogin(credential: string) {
    try {
      // Decode the JWT token to get user information
      const decodedToken = jwtDecode<GoogleUserInfo>(credential);

      console.log({
        issuedAt: new Date(decodedToken.iat * 1000).toISOString(),
        expiresAt: new Date(decodedToken.exp * 1000).toISOString(),
        issuer: decodedToken.iss,
        audience: decodedToken.aud,
        authorizedParty: decodedToken.azp,
      });
      // Prepare user info for storage
      const userInfo: StoredUserInfo = {
        googleSub: decodedToken.sub,
        email: decodedToken.email,
        emailVerified: decodedToken.email_verified,
        name: decodedToken.name,
        firstName: decodedToken.given_name,
        lastName: decodedToken.family_name,
        profilePicture: decodedToken.picture,
        locale: decodedToken.locale,
        provider: 'google',
        lastLogin: new Date().toISOString(),
      };

      // Store user info in localStorage
      storeUserInfo(userInfo);

      // Log the stored information
      console.log('Stored User Information:', userInfo);

      // Get stored user info
      // const storedUserInfo = localStorage.getItem('user_info');
      // const userInfo = storedUserInfo ? JSON.parse(storedUserInfo) : undefined;

      // const response = await fetch(`${API_CONFIG.baseUrl}/auth/google`, {
      const baseURL = API_CONFIG.useMock ? '/api/v1' : import.meta.env.VITE_API_URL || '/api/v1';
      const response = await fetch(`${baseURL}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userInfo),
      });

      if (!response.ok) {
        throw new Error('Google authentication failed');
      }

      const data = await response.json();

      // Store the token
      authService.setToken(credential);

      // Fetch and store preferences
      const settings = await getSettings();
      preferencesService.savePreferencesToStorage(settings.preferences);

      return data;
    } catch (error) {
      console.error('Google authentication error:', error);
      throw error;
    }
  },
};
